import JSZip from 'jszip';
import { format } from 'date-fns';
import { extractFileName } from './fileHelper';

/**
 * Sanitizes a folder or file name by removing invalid characters
 * @param {string} name - The name to sanitize
 * @returns {string} Sanitized name with invalid characters replaced by underscores
 */
const sanitizeFolderName = (name) => {
  if (!name) return 'File';
  return name.replace(/[/\\:*?"<>|]/g, '_');
};

/**
 * Extracts and cleans a filename from a file path
 * Uses the extractFileName utility to remove UUIDs for cleaner display
 * @param {string} filePath - Relative file path (e.g., "file-submission-session/File.zip-uuid.zip")
 * @returns {string} Clean filename without UUID
 */
const extractCleanFileName = (filePath) => {
  return extractFileName(filePath, 'file');
};

/**
 * Fetches a file from a URL with timeout and retry logic
 * @param {string} url - Full URL to the file
 * @param {number} retries - Number of retry attempts on failure
 * @returns {Promise<Blob>} File blob data
 * @throws {Error} If fetch fails after all retry attempts
 */
const fetchFileWithRetry = async (url, retries = 1) => {
  const timeout = 30000; // 30 seconds timeout
  let lastError;

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      // eslint-disable-next-line no-await-in-loop
      const response = await fetch(url, {
        signal: controller.signal,
        mode: 'cors',
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // eslint-disable-next-line no-await-in-loop
      return await response.blob();
    } catch (error) {
      lastError = error;
      // Don't retry on last attempt
      if (attempt < retries) {
        // Wait briefly before retry (exponential backoff)
        // eslint-disable-next-line no-await-in-loop, no-promise-executor-return
        await new Promise((resolve) => {
          setTimeout(resolve, 1000 * (attempt + 1));
        });
      }
    }
  }

  throw lastError;
};

/**
 * Processes submission records and transforms them into download tasks
 * Skips users with empty filePaths arrays (no files to download)
 * @param {Array} submissions - Array of submission records from exportSubmissionSessions.data
 * @param {string} baseFileUrl - Base URL from process.env.REACT_APP_FILE_URL
 * @returns {Array<Object>} Array of download tasks with { userName, fileUrl, fileName, filePath }
 */
const processSubmissionRecords = (submissions, baseFileUrl) => {
  const tasks = [];

  submissions.forEach((submission) => {
    // Skip users with no files
    if (!submission.filePaths || submission.filePaths.length === 0) {
      return;
    }

    // Create a task for each file
    submission.filePaths.forEach((filePath) => {
      tasks.push({
        userName:
          `${submission.userId} - ${submission.userName}` || 'Unknown User',
        fileUrl: `${baseFileUrl}${filePath}`,
        fileName: extractCleanFileName(filePath),
        filePath,
      });
    });
  });

  return tasks;
};

/**
 * Downloads files with controlled concurrency using a promise pool pattern
 * Calls onProgress callback for each completed download
 * Continues on individual failures and collects error details
 * @param {Array<Object>} tasks - Array of download tasks
 * @param {number} concurrency - Maximum number of simultaneous downloads
 * @param {Function} onProgress - Callback function (current, total)
 * @returns {Promise<Object>} Object with { successes: [...], failures: [...] }
 */
const downloadWithConcurrency = async (tasks, concurrency, onProgress) => {
  const successes = [];
  const failures = [];
  let completedCount = 0;

  // Create a pool of concurrent downloads
  const executeTask = async (task) => {
    try {
      const blob = await fetchFileWithRetry(task.fileUrl, 1);
      completedCount += 1;
      onProgress(completedCount, tasks.length);
      return { success: true, task, blob };
    } catch (error) {
      completedCount += 1;
      onProgress(completedCount, tasks.length);
      return {
        success: false,
        task,
        error: error.message || 'Download failed',
      };
    }
  };

  // Process tasks with concurrency limit using reduce for sequential promise chaining
  const executing = [];

  // eslint-disable-next-line no-restricted-syntax
  for (const task of tasks) {
    const promise = executeTask(task).then((result) => {
      if (result.success) {
        successes.push({ ...result.task, blob: result.blob });
      } else {
        failures.push({
          userName: result.task.userName,
          fileName: result.task.fileName,
          error: result.error,
        });
      }
      // Remove from executing pool
      executing.splice(executing.indexOf(promise), 1);
    });

    executing.push(promise);

    // If we've reached concurrency limit, wait for one to finish
    if (executing.length >= concurrency) {
      // eslint-disable-next-line no-await-in-loop
      await Promise.race(executing);
    }
  }

  // Wait for all remaining downloads to complete
  await Promise.all(executing);

  return { successes, failures };
};

/**
 * Creates a ZIP file structure from downloaded files
 * Organizes files into folders by userName
 * Handles duplicate folder names and filenames within folders
 * @param {Array<Object>} downloadedFiles - Array of { userName, fileName, blob }
 * @returns {JSZip} JSZip instance with organized file structure
 */
const createZipStructure = (downloadedFiles) => {
  const zip = new JSZip();

  // Track folder names to handle duplicates
  const folderCountMap = new Map();

  /**
   * Gets a unique folder name by appending counter for duplicates
   * @param {string} userName - Original user name
   * @returns {string} Unique folder name
   */
  const getFolderName = (userName) => {
    const sanitized = sanitizeFolderName(userName);
    const count = folderCountMap.get(sanitized) || 0;
    folderCountMap.set(sanitized, count + 1);
    return count === 0 ? sanitized : `${sanitized} (${count + 1})`;
  };

  // Track filenames per user folder to handle duplicates
  const userFilesMap = new Map(); // Map<folderName, Set<filename>>

  /**
   * Gets a unique filename within a folder by appending counter for duplicates
   * @param {string} folderName - Folder name
   * @param {string} fileName - Original filename
   * @returns {string} Unique filename within the folder
   */
  const getUniqueFileName = (folderName, fileName) => {
    if (!userFilesMap.has(folderName)) {
      userFilesMap.set(folderName, new Set());
    }

    const filesInFolder = userFilesMap.get(folderName);
    let uniqueName = fileName;
    let counter = 1;

    while (filesInFolder.has(uniqueName)) {
      const lastDotIndex = fileName.lastIndexOf('.');
      if (lastDotIndex > 0) {
        const ext = fileName.substring(lastDotIndex);
        const base = fileName.substring(0, lastDotIndex);
        uniqueName = `${base} (${counter})${ext}`;
      } else {
        uniqueName = `${fileName} (${counter})`;
      }
      counter += 1;
    }

    filesInFolder.add(uniqueName);
    return uniqueName;
  };

  // Group files by userName first to determine folder names
  const userGroups = new Map();
  downloadedFiles.forEach((file) => {
    if (!userGroups.has(file.userName)) {
      userGroups.set(file.userName, []);
    }
    userGroups.get(file.userName).push(file);
  });

  // Create folders and add files
  userGroups.forEach((files, userName) => {
    const folderName = getFolderName(userName);

    files.forEach((file) => {
      const uniqueFileName = getUniqueFileName(folderName, file.fileName);
      const filePath = `${folderName}/${uniqueFileName}`;
      zip.file(filePath, file.blob);
    });
  });

  return zip;
};

/**
 * Generates a ZIP blob and triggers browser download
 * Uses streaming mode for memory efficiency
 * No compression applied (files already compressed)
 * @param {JSZip} zip - JSZip instance with files
 * @returns {Promise<void>}
 */
const generateAndDownloadZip = async (zip) => {
  // Generate ZIP blob with no compression (files already compressed)
  const blob = await zip.generateAsync({
    type: 'blob',
    compression: 'STORE', // No compression
    streamFiles: true, // Memory-efficient streaming
  });

  // Create download link
  const filename = `file-submissions-export-${format(new Date(), 'yyyyMMdd-HHmmss')}.zip`;
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up blob URL
  URL.revokeObjectURL(url);
};

/**
 * Downloads files from URLs, organizes them into folders by userName,
 * creates a ZIP file, and triggers browser download
 *
 * Main workflow:
 * 1. Transform submission records into download tasks
 * 2. Download files with controlled concurrency (5 simultaneous)
 * 3. Create ZIP structure organized by user folders
 * 4. Generate ZIP and trigger browser download
 * 5. Return statistics about the export
 *
 * @param {Array} submissions - Array of submission records from exportSubmissionSessions.data
 * @param {string} baseFileUrl - Base URL from process.env.REACT_APP_FILE_URL
 * @param {Function} onProgress - Callback for progress updates (current, total)
 * @returns {Promise<Object>} Resolves with { totalFiles, successful, failed }
 * @throws {Error} If ZIP generation fails (download failures don't throw, they're collected)
 */
export default async function exportAsZip(
  submissions,
  baseFileUrl,
  onProgress
) {
  // 1. Transform records to download tasks
  const tasks = processSubmissionRecords(submissions, baseFileUrl);

  if (tasks.length === 0) {
    throw new Error('No files found to export');
  }

  // 2. Download files with controlled concurrency
  const { successes, failures } = await downloadWithConcurrency(
    tasks,
    5, // Concurrency limit
    onProgress
  );

  if (successes.length === 0) {
    throw new Error('All file downloads failed');
  }

  // 3. Create ZIP structure
  const zip = createZipStructure(successes);

  // 4. Generate and download ZIP
  await generateAndDownloadZip(zip);

  // 5. Return statistics
  return {
    totalFiles: tasks.length,
    successful: successes.length,
    failed: failures.length,
  };
}
